/// <amd-module name="VSS/Platform/Settings" />
import { IVssService, Services, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";
import { ISettingsRestClient } from "./RestClient/Settings";
import { UserClaims, userHasClaim } from "./UserClaims";

/**
 * Whether settings are applied to the current user or to all users in the host
 */
export const enum SettingsUserScope {
    /**
     * Settings for the current user
     */
    Me,

    /**
     * Shared settings for all users in this host
     */
    Host,

    /**
     * Settings for current user which span organizations
     */
    GlobalMe
}

/**
 * Service for interacting with Settings REST Endpoint which handles anonymous/public users
 * by not calling the server since the server endpoint is not open to public.
 */
export interface ISettingsService extends IVssService {
    /**
     * Synchronous method to get a setting entry from the settings emitted from local data providers. Returns undefined
     * if the value was not present.
     *
     * @param {string} key - Settings key.
     * @param {string} userScope - User-Scope at which to get the value. Should be Me for the current user or Host for all users.
     * @param {string} scopeName - Scope at which to get the setting for (e.g. "project" or "team")
     * @param {string} scopeValue - Value of the scope (e.g. the project or team id)
     * @return Promise<{ [key: string] : any; }>
     */
    getEntry<T>(key: string, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): T | undefined;

    /**
     * Get all setting entries by making a REST call if the data was not already included in local data provider data.
     *
     * @param {string} key - Key under which to filter all the entries
     * @param {string} userScope - User-Scope at which to get the value. Should be Me for the current user or Host for all users.
     * @param {string} scopeName - Scope at which to get the setting for (e.g. "project" or "team")
     * @param {string} scopeValue - Value of the scope (e.g. the project or team id)
     * @return Promise<{ [key: string] : any; }>
     */
    getEntriesAsync(key: string, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): Promise<{ [key: string]: any }>;

    /**
     * Set the specified settings entries
     *
     * @param {{ [key: string] : any; }} entries - The entries to set
     * @param {string} userScope - User-Scope at which to set the values. Should be Me for the current user or Host for all users.
     * @param {string} scopeName - Scope at which to set the settings on (e.g. "project" or "team")
     * @param {string} scopeValue - Value of the scope (e.g. the project or team id)
     * @return Promise<void>
     */
    setEntries(entries: { [key: string]: any }, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): Promise<void>;

    /**
     * Remove the entry or entries under the specified path
     *
     * @param {string} key - Root key of the entry or entries to remove
     * @param {string} userScope - User-Scope at which to remove the value. Should be Me for the current user or Host for all users.
     * @param {string} scopeName - Scope at which to get the setting for (e.g. "project" or "team")
     * @param {string} scopeValue - Value of the scope (e.g. the project or team id)
     * @return Promise<void>
     */
    removeEntries(key: string, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): Promise<void>;
}

const getFallbackPromise = Promise.resolve({ value: {}, count: 0 });
const setFallbackPromise = Promise.resolve();

class SettingsService extends VssService implements ISettingsService {
    private _httpClient: ISettingsRestClient;
    private _canCallSettingsEndpoint: boolean;

    public getEntry<T>(key: string, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): T | undefined {
        const scopeValues = this.getEntriesForKey(userScope, scopeName, scopeValue);
        if (scopeValues) {
            const entry = scopeValues[key];
            if (entry !== undefined) {
                return entry;
            }

            // Look at entries for parent paths
            const keyParts = key.split("/");
            for (let i = keyParts.length - 2; i >= 0; i--) {
                const joinedKey = keyParts.slice(0, i + 1).join("/");
                const parentEntry = scopeValues[joinedKey];
                if (parentEntry) {
                    // We found an entry for a parent path, now select the appropriate object through this item's keys
                    let entry = parentEntry;
                    for (let j = i + 1; j < keyParts.length && entry; j++) {
                        entry = entry[keyParts[j]];
                    }
                    return entry as T;
                }
            }
        }

        return undefined;
    }

    public getEntriesAsync(key: string, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): Promise<{ [key: string]: any }> {
        if (!this.canCallSettingsEndpoint) {
            return getFallbackPromise;
        }

        let getEntriesPromise: Promise<{ [key: string]: any }>;
        if (scopeName) {
            getEntriesPromise = this.httpClient.getEntriesForScope(this.userScopeToString(userScope), scopeName, scopeValue!, key);
        } else {
            getEntriesPromise = this.httpClient.getEntries(this.userScopeToString(userScope), key);
        }
        return getEntriesPromise.then(e => this.updateLocalEntries(key, e, userScope, scopeName, scopeValue));
    }

    public setEntries(entries: { [key: string]: any }, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): Promise<void> {
        if (!this.canCallSettingsEndpoint) {
            return setFallbackPromise;
        }

        for (const key in entries) {
            this.updateLocalEntries(key, entries[key], userScope, scopeName, scopeValue);
        }

        if (scopeName) {
            return this.httpClient.setEntriesForScope(entries, this.userScopeToString(userScope), scopeName, scopeValue!);
        } else {
            return this.httpClient.setEntries(entries, this.userScopeToString(userScope));
        }
    }

    public removeEntries(key: string, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): Promise<void> {
        if (!this.canCallSettingsEndpoint) {
            return setFallbackPromise;
        }

        this.updateLocalEntries(key, undefined, userScope, scopeName, scopeValue);

        if (scopeName) {
            return this.httpClient.removeEntriesForScope(this.userScopeToString(userScope), scopeName, scopeValue!, key);
        } else {
            return this.httpClient.removeEntries(this.userScopeToString(userScope), key);
        }
    }

    private userScopeToString(userScope: SettingsUserScope) {
        if (userScope === SettingsUserScope.GlobalMe) {
            return "globalme";
        } else if (userScope === SettingsUserScope.Host) {
            return "host";
        }

        return "me";
    }

    private get httpClient(): ISettingsRestClient {
        if (!this._httpClient) {
            this._httpClient = this.pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
        }

        return this._httpClient;
    }

    private get canCallSettingsEndpoint(): boolean {
        if (this._canCallSettingsEndpoint === undefined) {
            this._canCallSettingsEndpoint = userHasClaim(this.pageContext, UserClaims.Member);
        }

        return this._canCallSettingsEndpoint;
    }

    private updateLocalEntries(key: string, value: any, userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): any {
        const scopeValues = this.getEntriesForKey(userScope, scopeName, scopeValue);
        if (scopeValues) {
            // Set this particular entry
            scopeValues[key] = value;

            // Add this entry to any parent item entries if they contain this key
            const keyParts = key.split("/");
            for (let i = keyParts.length - 2; i >= 0; i--) {
                const joinedKey = keyParts.slice(0, i + 1).join("/");
                const parentEntry = scopeValues[joinedKey];
                if (parentEntry) {
                    // Create any necessary children
                    let entry = parentEntry;
                    for (let j = i + 1; j < keyParts.length - 1 && entry; j++) {
                        let childEntry = entry[keyParts[j]];
                        if (!childEntry) {
                            childEntry = entry[keyParts[j]] = {};
                        }
                        entry = childEntry;
                    }

                    // Set the item
                    entry[keyParts[keyParts.length - 1]] = value;
                }
            }
        }
        return value;
    }

    private getEntriesForKey(userScope: SettingsUserScope, scopeName?: string, scopeValue?: string): { [settingsKey: string]: any } | undefined {
        let entries: { [settingsKey: string]: any } | undefined;

        if (this.canCallSettingsEndpoint) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const settingsSharedData = contributionService.getSharedData<{ [scopeKey: string]: { [settingsKey: string]: any } }>("_settings");
            if (settingsSharedData) {
                let scopeKey = this.userScopeToString(userScope);
                if (scopeName) {
                    scopeKey += ";" + scopeName + ";" + (scopeValue || "");
                }

                entries = settingsSharedData[scopeKey];
            }
        }

        return entries;
    }
}

Services.add("ISettingsService", { serviceFactory: SettingsService });
